import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import IconHeart from "@/icons/icon-heart";
import Button from "~/components/common/button";
import EVAErrors from "~/components/form/eva-errors";
import InputField from "~/components/form/input-field";
import { wishlistNameFields, wishlistNameFieldSchema } from "~/shared/wishlist/wishlist.model";
import { wishlistNameMaxlength, wishlistNameMinlength } from "~/shared/wishlist/zod-schemas.class";

import Dialog from "../../common/dialog";
import Icon from "../../common/icon";
import editWishlistDialogStyles from "./edit-wishlist-dialog.module.scss";
import styles from "./wishlist.module.scss";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  error?: any;
  cleanError?: () => void;
  submit: SubmitHandler<wishlistNameFields>;
  defaultName: string;
};

export default function EditWishlistDialog({ isOpen, onClose, cleanError, submit, defaultName, error }: Props) {
  const t = useTranslations();

  const {
    register,
    handleSubmit,
    clearErrors,
    setValue,
    formState: { errors },
  } = useForm<wishlistNameFields>({
    mode: "onBlur",
    reValidateMode: "onBlur",
    resolver: zodResolver(wishlistNameFieldSchema),
  });

  useEffect(() => {
    setValue("wishlistName", defaultName);
  }, [defaultName, setValue]);

  return (
    <Dialog
      isOpen={isOpen}
      title={
        <>
          <Icon icon={IconHeart} />
          <span>{t("generic.myaccount.wishlist.edit_dialog_title")}</span>
        </>
      }
      onClose={onClose}
      onAfterOpen={cleanError}
      customStyles={editWishlistDialogStyles}
    >
      <form onSubmit={handleSubmit(submit)} className={styles.form}>
        <InputField
          {...register("wishlistName")}
          type="text"
          label={t("generic.myaccount.wishlist.field")}
          error={
            "wishlistName" in errors
              ? t(errors.wishlistName?.message, {
                  fieldName: t("generic.myaccount.wishlist.field"),
                  maxLength: wishlistNameMaxlength,
                  minLength: wishlistNameMinlength,
                })
              : undefined
          }
          clearValue={() => {
            cleanError?.();
            setValue("wishlistName", "");
            clearErrors("wishlistName");
          }}
        />
        <EVAErrors error={error} />
        <Button className={styles.submitModal} variant="secondary" type="submit">
          {t("generic.save")}
        </Button>
      </form>
    </Dialog>
  );
}
